"use client"

import { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { AuthGuard } from "@/components/auth-guard"
import { AppSidebar } from "@/components/app-sidebar"
import { ChartAreaInteractive, type MetricType } from "@/components/chart-area-interactive"
import { AnalyticsDashboard } from "@/components/analytics-dashboard"
import { AnalyticsPeriodSelector, type AnalyticsPeriod, type CustomDateRange } from "@/components/analytics-period-selector"
import { SiteHeader } from "@/components/site-header"
import {
  SidebarInset,
  SidebarProvider,
} from "@/components/ui/sidebar"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { useUser } from "@/components/user-context"
import { canViewAnalytics } from "@/lib/permissions"
import { hasElevatedViewAccess } from "@/lib/workspace-permissions"
import { useRouter } from "next/navigation"
import { analyticsService } from "@/lib/analytics-service"
import { useAnalyticsData } from "@/hooks/use-analytics-data"
import { initializeAnalyticsState, saveToLocalStorage } from "@/lib/analytics-state"
import { useAnalyticsPeriod } from "@/hooks/use-analytics-period"
import { getEvents, getSessions, type AnalyticsEvent, type AnalyticsSession } from "@/lib/api-v3"
import { normalizeEventsPayload, normalizeSessionsPayload } from "@/lib/analytics-mappers"
import { getTokens } from "@/lib/auth"
import { getEventName, formatEventDetails } from "@/app/analytics/event-mappings"
import { eventsColumns, type EventRow } from "@/app/analytics/events-columns"
import { sessionsColumns, type SessionRow } from "@/app/analytics/sessions-columns"
import { sessionEventsColumns, type SessionEventRow } from "@/app/analytics/session-events-columns"
import { DataTable } from "@/app/scenarios/data-table"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/components/ui/drawer"
import { ArrowRight, RefreshCw, X } from "lucide-react"
import Link from "next/link"

function isPermissionError(error: string): boolean {
  const lower = error.toLowerCase()
  return lower.includes('permission') || lower.includes('forbidden') || lower.includes('403')
}

function mapEventsToRows(events: AnalyticsEvent[]): EventRow[] {
  return (events || []).map((e) => {
    const eventTime = new Date(e.time)
    const currentYear = new Date().getFullYear()
    const eventYear = eventTime.getFullYear()
    const day = String(eventTime.getDate()).padStart(2, '0')
    const month = String(eventTime.getMonth() + 1).padStart(2, '0')
    const hour = String(eventTime.getHours()).padStart(2, '0')
    const minute = String(eventTime.getMinutes()).padStart(2, '0')
    let dateTime: string
    if (eventYear === currentYear) {
      dateTime = `${day}.${month} ${hour}:${minute}`
    } else {
      const year = String(eventYear).slice(-2)
      dateTime = `${day}.${month}.${year} ${hour}:${minute}`
    }
    const eventCode = e.name || `Событие типа ${e.type}`
    const eventName = getEventName(eventCode)
    const details = formatEventDetails(eventCode, e.value)
    return {
      id: e.id,
      user: e.userLogin || `Пользователь #${e.userId}`,
      dateTime,
      eventCode,
      eventName,
      details,
      userId: e.userId,
      userLogin: e.userLogin,
      name: e.name,
      time: e.time,
      timeZone: e.timeZone,
      type: e.type,
      value: e.value,
      groupId: e.groupId,
      deviceId: e.deviceId,
      platform: e.platform,
      userAgent: e.userAgent,
      sessionId: e.sessionId ?? null,
    }
  })
}

function mapSessionsToRows(sessions: AnalyticsSession[]): SessionRow[] {
  const formatDuration = (begin: number, end: number) => {
    const ms = Math.max(0, end - begin)
    const totalSec = Math.floor(ms / 1000)
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    if (h > 0) return `${h}ч ${m}мин`
    if (m > 0) return `${m}мин ${s}с`
    return `${s}с`
  }
  const parseDevice = (ua: string | null) => {
    if (!ua) return '—'
    if (/iPhone|iPad|iOS/i.test(ua)) return 'iOS'
    if (/Android/i.test(ua)) return 'Android'
    if (/Windows/i.test(ua)) return 'Windows'
    if (/Mac/i.test(ua)) return 'macOS'
    return ua.slice(0, 40)
  }
  return (sessions || []).map((s) => {
    const t = new Date(s.beginTime)
    const cy = new Date().getFullYear()
    const d = String(t.getDate()).padStart(2, '0')
    const mo = String(t.getMonth() + 1).padStart(2, '0')
    const h = String(t.getHours()).padStart(2, '0')
    const mi = String(t.getMinutes()).padStart(2, '0')
    const dateTime = t.getFullYear() === cy
      ? `${d}.${mo} ${h}:${mi}`
      : `${d}.${mo}.${String(t.getFullYear()).slice(-2)} ${h}:${mi}`
    return {
      id: s.id,
      user: s.userLogin || `Пользователь #${s.userId}`,
      dateTime,
      duration: formatDuration(s.beginTime, s.endTime),
      device: parseDevice(s.userAgent),
      userAgent: s.userAgent || '—',
      userId: s.userId,
      userLogin: s.userLogin,
      groupId: s.groupId,
      deviceId: s.deviceId,
      platform: s.platform,
      timeZone: s.timeZone,
      beginTime: s.beginTime,
      endTime: s.endTime,
      isClosed: s.isClosed,
      isPaused: s.isPaused,
    }
  })
}

function mapSessionEventsToRows(events: AnalyticsEvent[], sessionBeginTime: number): SessionEventRow[] {
  const fmtElapsed = (ms: number) => {
    const totalSec = Math.floor(Math.abs(ms) / 1000)
    const mss = Math.abs(ms) % 1000
    const h = Math.floor(totalSec / 3600)
    const m = Math.floor((totalSec % 3600) / 60)
    const s = totalSec % 60
    const parts: string[] = []
    if (h > 0) parts.push(`${h}ч`)
    if (m > 0) parts.push(`${m}мин`)
    parts.push(`${s}с`)
    parts.push(`${mss}мс`)
    return parts.join(' ')
  }
  return (events || []).map((e, i) => {
    const timeFromStart = fmtElapsed(e.time - sessionBeginTime)
    const timeBetween = i < events.length - 1
      ? fmtElapsed(Math.abs(events[i + 1].time - e.time))
      : '—'
    const eventCode = e.name || `Событие типа ${e.type}`
    return {
      id: e.id,
      timeFromStart,
      timeBetween,
      eventCode,
      eventName: getEventName(eventCode),
      details: formatEventDetails(eventCode, e.value),
      userId: e.userId,
      userLogin: e.userLogin,
      name: e.name,
      time: e.time,
      timeZone: e.timeZone,
      type: e.type,
      value: e.value,
      groupId: e.groupId,
      deviceId: e.deviceId,
      platform: e.platform,
      userAgent: e.userAgent,
      sessionId: e.sessionId ?? null,
    }
  })
}

export default function Page() {
  const router = useRouter()
  const { userGroup, userWorkspace, isLoading: isUserLoading, isGroupLoaded, user } = useUser()

  // Редирект если нет прав по роли
  useEffect(() => {
    if (isUserLoading || !isGroupLoaded) return
    const predefinedRole = userWorkspace?.role?.predefinedRole
    if (!canViewAnalytics(userGroup) && !hasElevatedViewAccess(predefinedRole)) {
      router.push('/areas')
    }
  }, [userGroup, userWorkspace, isUserLoading, isGroupLoaded, router])

  // Редирект при permission ошибке от analytics-сервиса (карточки обзора)
  const { data: analyticsServiceData } = useAnalyticsData()
  useEffect(() => {
    if (analyticsServiceData.error && isPermissionError(analyticsServiceData.error)) {
      router.push('/areas')
    }
  }, [analyticsServiceData.error, router])

  // Редирект при permission ошибке от графика
  const handleChartError = useCallback((error: string) => {
    if (isPermissionError(error)) {
      router.push('/areas')
    }
  }, [router])

  const {
    period: analyticsPeriod,
    setPeriod: setAnalyticsPeriod,
    includeAdmins,
    setIncludeAdmins,
    customRange,
    setCustomRange,
  } = useAnalyticsPeriod(user?.id)

  // ── Блок активности: последние события ───────────────────────────────────
  const [activityTab, setActivityTab] = useState<'events' | 'sessions'>('events')
  const [eventsData, setEventsData] = useState<EventRow[]>([])
  const [eventsLoading, setEventsLoading] = useState(false)
  const eventsAbortRef = useRef<AbortController | null>(null)

  // Сессии (визиты)
  const [sessionsData, setSessionsData] = useState<SessionRow[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const sessionsAbortRef = useRef<AbortController | null>(null)

  // Drawer событий сессии
  const [sessionDrawerOpen, setSessionDrawerOpen] = useState(false)
  const [selectedSession, setSelectedSession] = useState<SessionRow | null>(null)
  const [sessionEventsData, setSessionEventsData] = useState<SessionEventRow[]>([])
  const [sessionEventsLoading, setSessionEventsLoading] = useState(false)

  const loadRecentEvents = useCallback(async (workspaceId?: number, withAdmins?: boolean) => {
    const tokens = getTokens()
    if (!tokens?.accessToken) return
    eventsAbortRef.current?.abort()
    const controller = new AbortController()
    eventsAbortRef.current = controller
    setEventsLoading(true)
    try {
      // Последние 50 событий за последние 7 дней
      const now = Date.now()
      const weekAgo = now - 7 * 24 * 60 * 60 * 1000
      const params: Parameters<typeof getEvents>[1] = {
        begin: weekAgo,
        end: now,
        take: 50,
        skip: 0,
        sort: 1,
        ignoreAdmins: !withAdmins,
      }
      if (workspaceId) params.workspace = workspaceId
      const raw = await getEvents(tokens.accessToken, params, controller)
      if (!controller.signal.aborted) {
        const { items } = normalizeEventsPayload(raw)
        setEventsData(mapEventsToRows(items))
      }
    } catch {
      // silently ignore
    } finally {
      if (!eventsAbortRef.current?.signal.aborted) {
        setEventsLoading(false)
      }
    }
  }, [])

  const loadRecentSessions = useCallback(async (workspaceId?: number, withAdmins?: boolean) => {
    const tokens = getTokens()
    if (!tokens?.accessToken) return
    sessionsAbortRef.current?.abort()
    const controller = new AbortController()
    sessionsAbortRef.current = controller
    setSessionsLoading(true)
    try {
      const now = Date.now()
      const weekAgo = now - 7 * 24 * 60 * 60 * 1000
      const params: Parameters<typeof getSessions>[1] = {
        begin: weekAgo,
        end: now,
        take: 50,
        skip: 0,
        sort: 1,
        ignoreAdmins: !withAdmins,
      }
      if (workspaceId) params.workspace = workspaceId
      const raw = await getSessions(tokens.accessToken, params, controller)
      if (!controller.signal.aborted) {
        const { items } = normalizeSessionsPayload(raw)
        setSessionsData(mapSessionsToRows(items))
      }
    } catch {
      // silently ignore
    } finally {
      if (!sessionsAbortRef.current?.signal.aborted) {
        setSessionsLoading(false)
      }
    }
  }, [])

  const loadSessionEvents = useCallback(async (session: SessionRow, workspaceId?: number) => {
    const tokens = getTokens()
    if (!tokens?.accessToken) return
    setSessionEventsLoading(true)
    setSessionEventsData([])
    try {
      const params: Parameters<typeof getEvents>[1] = {
        session: session.id,
        take: 200,
        skip: 0,
        sort: 0, // сначала старые
        ignoreAdmins: false,
      }
      if (workspaceId) params.workspace = workspaceId
      const raw = await getEvents(tokens.accessToken, params)
      const { items } = normalizeEventsPayload(raw)
      setSessionEventsData(mapSessionEventsToRows(items, session.beginTime))
    } catch {
      // silently ignore
    } finally {
      setSessionEventsLoading(false)
    }
  }, [])

  const initialState = useMemo(() => initializeAnalyticsState(user?.id), [user?.id])
  const [chartType, setChartType] = useState<'area' | 'bar'>(initialState.chartType ?? 'area')
  const [metricType, setMetricType] = useState<MetricType>(initialState.metricType ?? 'scenarios')

  // Persist chart settings
  useEffect(() => {
    if (!user?.id) return
    const s = { chartType, metricType }
    const existing = initializeAnalyticsState(user.id)
    saveToLocalStorage({ ...existing, ...s }, user.id)
  }, [chartType, metricType, user?.id])

  const handlePeriodChange = (period: AnalyticsPeriod, newCustomRange?: CustomDateRange) => {
    setAnalyticsPeriod(period)
    if (newCustomRange) setCustomRange(newCustomRange)
    analyticsService.loadAnalytics({ period, includeAdmins, customRange: newCustomRange })
  }

  const handleIncludeAdminsChange = (include: boolean) => {
    setIncludeAdmins(include)
    analyticsService.loadAnalytics({ period: analyticsPeriod, includeAdmins: include })
    loadRecentEvents(userWorkspace?.workspaceId, include)
    loadRecentSessions(userWorkspace?.workspaceId, include)
  }

  // Загружаем аналитику только после проверки прав, чтобы избежать 403 при смене аккаунта
  useEffect(() => {
    if (isUserLoading || !isGroupLoaded) return
    const predefinedRole = userWorkspace?.role?.predefinedRole
    if (!canViewAnalytics(userGroup) && !hasElevatedViewAccess(predefinedRole)) return
    analyticsService.loadAnalytics({ period: analyticsPeriod, includeAdmins, customRange })
    loadRecentEvents(userWorkspace?.workspaceId, includeAdmins)
    loadRecentSessions(userWorkspace?.workspaceId, includeAdmins)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroupLoaded, userGroup, userWorkspace])

  return (
    <AuthGuard>
      <SidebarProvider
        style={
          {
            "--sidebar-width": "calc(var(--spacing) * 72)",
            "--header-height": "calc(var(--spacing) * 12)",
          } as React.CSSProperties
        }
      >
        <AppSidebar variant="sidebar" className="border-r" />
        <SidebarInset className="rounded-none border-none shadow-none h-screen-safe flex flex-col">
          <SiteHeader />
          <div className="flex-1 min-h-0">
            <ScrollArea className="h-full">
              <div className="@container/main flex flex-col gap-2">
                <AnalyticsPeriodSelector
                  period={analyticsPeriod}
                  includeAdmins={includeAdmins}
                  customRange={customRange}
                  onPeriodChange={handlePeriodChange}
                  onIncludeAdminsChange={handleIncludeAdminsChange}
                />
                <div className="flex flex-col gap-4 pb-6">
                  <div className="px-4 lg:px-6">
                    <div className="sticky top-15 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 z-[2] py-2 -mx-4 lg:-mx-6 px-4 lg:px-6 border-b border-border/50">
                      <h2 className="text-lg font-semibold">Обзор</h2>
                    </div>
                    <div className="-mx-4 lg:-mx-6 mt-4">
                      <AnalyticsDashboard />
                    </div>
                  </div>
                  <div className="px-4 lg:px-6 mt-8">
                    <div className="sticky top-15 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 z-[2] py-2 -mx-4 lg:-mx-6 px-4 lg:px-6 border-b border-border/50">
                      <h2 className="text-lg font-semibold">Статистика</h2>
                    </div>
                    <div className="mt-4">
                      <ChartAreaInteractive
                        includeAdmins={includeAdmins}
                        period={analyticsPeriod}
                        customRange={customRange}
                        chartType={chartType}
                        metricType={metricType}
                        workspaceId={userWorkspace?.workspaceId ?? null}
                        onChartTypeChange={setChartType}
                        onMetricTypeChange={setMetricType}
                        onError={handleChartError}
                      />
                    </div>
                  </div>

                  {/* ── Блок активности: события + визиты — скрыт
                  <div className="px-4 lg:px-6 mt-8">
                    <Tabs value={activityTab} onValueChange={(v) => setActivityTab(v as 'events' | 'sessions')}>
                      <div className="sticky top-15 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 z-[2] py-2 -mx-4 lg:-mx-6 px-4 lg:px-6 border-b border-border/50">
                        <div className="flex items-center justify-between">
                          <TabsList className="h-8">
                            <TabsTrigger value="events" className="text-sm h-7">События</TabsTrigger>
                            <TabsTrigger value="sessions" className="text-sm h-7">Визиты</TabsTrigger>
                          </TabsList>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 gap-1.5 text-muted-foreground"
                              onClick={() => {
                                if (activityTab === 'events') loadRecentEvents(userWorkspace?.workspaceId, includeAdmins)
                                else loadRecentSessions(userWorkspace?.workspaceId, includeAdmins)
                              }}
                              disabled={activityTab === 'events' ? eventsLoading : sessionsLoading}
                            >
                              <RefreshCw className={`h-3.5 w-3.5 ${(activityTab === 'events' ? eventsLoading : sessionsLoading) ? 'animate-spin' : ''}`} />
                              Обновить
                            </Button>
                            {activityTab === 'events' && (
                              <Button variant="ghost" size="sm" className="h-7 gap-1.5 text-muted-foreground" asChild>
                                <Link href="/analytics/events-log">
                                  Все события
                                  <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      <TabsContent value="events" className="mt-4">
                        <DataTable
                          columns={eventsColumns}
                          data={eventsData}
                          isLoading={eventsLoading}
                          verticalBorders={false}
                          enableColumnResize={false}
                          enableColumnReorder={false}
                          enableRowReorder={false}
                          enableRowSelection={false}
                          autoColumnWidth={true}
                          showPagination={false}
                          showSearch={false}
                          showSortSelector={false}
                        />
                      </TabsContent>

                      <TabsContent value="sessions" className="mt-4">
                        <DataTable
                          columns={sessionsColumns}
                          data={sessionsData}
                          isLoading={sessionsLoading}
                          verticalBorders={false}
                          enableColumnResize={false}
                          enableColumnReorder={false}
                          enableRowReorder={false}
                          enableRowSelection={false}
                          autoColumnWidth={true}
                          showPagination={false}
                          showSearch={false}
                          showSortSelector={false}
                          onRowClick={(session: SessionRow) => {
                            setSelectedSession(session)
                            setSessionDrawerOpen(true)
                            loadSessionEvents(session, userWorkspace?.workspaceId)
                          }}
                        />
                      </TabsContent>
                    </Tabs>
                  </div>
                  ── */}

                  {/* Drawer событий визита — скрыт (таб «Визиты» закомментирован)
                  <Drawer open={sessionDrawerOpen} onOpenChange={setSessionDrawerOpen}>
                    <DrawerContent className="h-[60vh] flex flex-col">
                      <div className="w-full flex flex-col h-full px-4 pb-10">
                        <DrawerHeader className="px-0 py-3 shrink-0">
                          <div className="flex items-center justify-between gap-4">
                            <DrawerTitle className="text-base flex items-baseline gap-2">
                              <span>События визита</span>
                              {selectedSession && (
                                <span className="text-xs font-normal text-muted-foreground">
                                  ({selectedSession.user} • {selectedSession.dateTime} • {selectedSession.duration})
                                </span>
                              )}
                            </DrawerTitle>
                            <DrawerClose asChild>
                              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0">
                                <X className="h-4 w-4" />
                                <span className="sr-only">Закрыть</span>
                              </Button>
                            </DrawerClose>
                          </div>
                        </DrawerHeader>
                        <div className="flex-1 min-h-0 overflow-auto">
                          <DataTable
                            columns={sessionEventsColumns}
                            data={sessionEventsData}
                            isLoading={sessionEventsLoading}
                            verticalBorders={false}
                            enableColumnResize={false}
                            enableColumnReorder={false}
                            enableRowReorder={false}
                            enableRowSelection={false}
                            autoColumnWidth={true}
                            showPagination={false}
                            showSearch={false}
                            showSortSelector={false}
                          />
                        </div>
                      </div>
                    </DrawerContent>
                  </Drawer>
                  */}
                </div>
              </div>
            </ScrollArea>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </AuthGuard>
  )
}
